"use client";

import { cn } from "@jesus-central-church/shared";
import { motion } from "motion/react";
import { Disc3, Headphones, Play, Volume2 } from "lucide-react";
import SpotifyEmbed from "./spotify-embed";
import { YoutubeEmbed } from "./youtube-embed";

interface MediaReleaseProps {
    subtitle: string;
    title: string;
    description: string;
    youtubeUrl?: string;
    spotifyUrl?: string;
    className?: string;
}

export function MediaRelease({
    subtitle,
    title,
    description,
    youtubeUrl,
    spotifyUrl,
    className,
}: MediaReleaseProps) {
    // Split title into words for staggered animation
    const titleWords = title.split(" ");

    return (
        <section
            className={cn(
                "relative min-h-screen overflow-hidden bg-boldness",
                className
            )}
        >
            {/* Clean Background */}
            <div className="pointer-events-none absolute inset-0">
                {/* Subtle top-left glow */}
                <div className="absolute -left-40 -top-40 h-[500px] w-[500px] rounded-full bg-strategy-gold/[0.03] blur-3xl" />

                {/* Subtle bottom-right glow */}
                <div className="absolute -bottom-40 -right-40 h-[400px] w-[400px] rounded-full bg-strategy-red/[0.02] blur-3xl" />
            </div>

            {/* Main Content */}
            <div className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 md:py-16 lg:px-8 lg:py-20">
                {/* Header Section */}
                <div className="mb-10 md:mb-14 lg:mb-16">
                    {/* Subtitle Badge */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                        className="mb-6 flex items-center gap-4"
                    >
                        <motion.div
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-strategy-gold/10 text-strategy-gold"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        >
                            <Disc3 size={20} />
                        </motion.div>
                        <div className="flex items-center gap-3">
                            <motion.div
                                className="h-px w-8 bg-gradient-to-r from-strategy-gold to-transparent"
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                            />
                            <span className="text-xs font-bold uppercase tracking-[0.3em] text-strategy-gold">
                                {subtitle}
                            </span>
                        </div>
                    </motion.div>

                    {/* Main Title with staggered word animation */}
                    <h1 className="mb-6 md:mb-8">
                        {titleWords.map((word, index) => (
                            <motion.span
                                key={index}
                                initial={{ opacity: 0, y: 50, rotateX: -40 }}
                                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                                transition={{
                                    duration: 0.7,
                                    delay: 0.2 + index * 0.1,
                                    ease: [0.25, 0.1, 0.25, 1],
                                }}
                                className="mr-4 inline-block font-heading text-5xl font-black uppercase italic tracking-tighter text-freedom sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl"
                                style={{
                                    textShadow: "0 0 80px rgba(243, 185, 99, 0.15)",
                                }}
                            >
                                {word}
                            </motion.span>
                        ))}
                    </h1>

                    {/* Description */}
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="max-w-2xl text-base leading-relaxed text-freedom/70 md:text-lg lg:text-xl"
                    >
                        {description}
                    </motion.p>

                    {/* Decorative line */}
                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 1, delay: 0.7, ease: "easeOut" }}
                        className="mt-8 h-px w-32 origin-left bg-gradient-to-r from-strategy-gold via-strategy-gold/50 to-transparent"
                    />
                </div>

                {/* Media Grid */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8 lg:items-stretch">
                    {/* YouTube Video Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                        className="flex flex-col lg:col-span-8"
                    >
                        <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-freedom/10 bg-boldness shadow-2xl shadow-black/40 md:rounded-3xl">
                            {/* Video container */}
                            <div className="relative aspect-video w-full overflow-hidden">
                                {youtubeUrl ? (
                                    <YoutubeEmbed youtubeUrl={youtubeUrl} className="h-full w-full" />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center bg-boldness">
                                        <div className="text-center">
                                            <Play size={48} className="mx-auto mb-4 text-freedom/20" />
                                            <p className="text-sm text-freedom/40">Video coming soon</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Video info bar */}
                            <div className="flex items-center justify-between border-t border-freedom/5 px-4 py-3 md:px-6 md:py-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-strategy-gold/10">
                                        <Volume2 size={14} className="text-strategy-gold" />
                                    </div>
                                    <span className="text-xs font-medium uppercase tracking-wider text-freedom/50">
                                        Officiële Video
                                    </span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Spotify Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                        className="flex flex-col lg:col-span-4"
                    >
                        {/* Spotify Card */}
                        <div className="flex-1 overflow-hidden rounded-2xl border border-freedom/10 bg-boldness p-6 shadow-xl md:rounded-3xl md:p-8">
                            {/* Header */}
                            <div className="mb-6 flex items-start justify-between">
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1DB954]/10 text-[#1DB954]">
                                    <Headphones size={24} />
                                </div>
                                <div className="flex items-center gap-2 rounded-full bg-freedom/5 px-3 py-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#1DB954]" />
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-freedom/50">
                                        Nu Beschikbaar
                                    </span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="mb-6">
                                <h3 className="mb-2 font-heading text-xl font-bold uppercase tracking-tight text-freedom md:text-2xl">
                                    Stream de Single
                                </h3>
                                <p className="text-sm leading-relaxed text-freedom/50">
                                    Luister nu op Spotify en alle andere platforms
                                </p>
                            </div>

                            {/* Spotify Embed */}
                            {spotifyUrl && (
                                <div className="relative w-full aspect-square">
                                    <SpotifyEmbed
                                        spotifyUrl={spotifyUrl}
                                        className="absolute inset-0 w-full h-full"
                                    />
                                </div>
                            )}
                        </div>


                    </motion.div>
                </div>
            </div>
        </section>
    );
}
